import React from 'react';
import cx from 'classnames';

import Information from '../Icons/Information';
import Checkmark from '../Icons/Checkmark';
import AlertWarning from '../Icons/AlertWarning';
import AlertError from '../Icons/AlertError';

import styles from './Note.module.scss';

export interface Props {
  /** Status indicator: A generic message or info item. Alias for the deprecated 'info' prop */
  attention?: boolean;
  /** Text and other (non-icon) content for the Note */
  children?: React.ReactNode;
  /** CSS class name(s) to apply to the Note */
  className?: Parameters<typeof cx>[0];
  /** Status indicator: An error or other important mishap */
  error?: boolean;
  /** Enables a wider min-width (2x the default) */
  fixed?: boolean;
  /** An icon to display (overrides the default status icon for whichever status indicator is enabled) */
  icon?: React.ReactNode;
  /** DEPRECATED: Use (and see description of) `attention` instead */
  info?: boolean;
  /** Height (in pixels) of the desired Note display. If no text is present (an empty Note), this measurement controls
   *  the width as well.
   */
  size?: 20 | 24 | 32;
  /** DEPRECATED: Use `size={24}` instead */
  small?: boolean;
  /** Whether or not to fill the Note with a background color */
  solid?: boolean;
  /** Status indicator: A process or task has finished successfully */
  success?: boolean;
  /** Status indicator: Something that needs attention or something may be wrong */
  warning?: boolean;
  /** Hide or show icon that appears before the content. */
  hasIcon?: boolean;
  /** Transforms letter casing for the text */
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
}

export const Note: React.FC<Props> = ({
  attention,
  children,
  className,
  error,
  fixed,
  icon,
  info, // DEPRECATED: use 'attention'
  size = 32,
  small,
  solid,
  success,
  warning,
  hasIcon = true,
  textTransform = 'uppercase',
}) => {
  let statusIcon = null;
  let statusClassName = null;

  switch (true) {
    case error:
      statusIcon = <AlertError className={styles.statusIcon} />;
      statusClassName = styles.error;
      break;
    case info:
    case attention:
      statusIcon = <Information className={styles.statusIcon} />;
      statusClassName = styles.attention;
      break;
    case success:
      statusIcon = <Checkmark className={styles.statusIcon} />;
      statusClassName = styles.success;
      break;
    case warning:
      statusIcon = <AlertWarning className={styles.statusIcon} />;
      statusClassName = styles.warning;
  }

  const withIcon = (icon && React.isValidElement(icon)) || statusIcon;

  return (
    <span
      role='note'
      className={cx(
        statusClassName,
        fixed && styles.fixed,
        (size === 24 || small) && styles.small,
        size === 20 && styles.xsmall,
        !children && styles.badge,
        solid && styles.solid,
        hasIcon && statusIcon && styles.withIcon,
        styles.note,
        styles[textTransform],
        className,
      )}
    >
      {hasIcon && withIcon && (
        <div className={cx(styles.iconWrapper)}>
          {icon
            ? React.cloneElement(icon as React.ReactElement, {
                className: cx(styles.statusIcon),
              })
            : statusIcon
            ? statusIcon
            : null}
        </div>
      )}
      {children}
    </span>
  );
};

Note.displayName = 'Note';
export default Note;
export { styles };
